import { supabase } from '../lib/supabase';
import type { TourDefinition, TourId, UserTourProgress, UserOnboarding } from '../types/onboarding';
import { logger } from '../lib/logger';

// ============================================================================
// Tour Definitions
// ============================================================================

export async function getTourDefinitions(): Promise<TourDefinition[]> {
  const { data, error } = await supabase
    .from('tour_definitions')
    .select('*')
    .order('sort_order');

  if (error) throw error;
  return data as TourDefinition[];
}

export async function getTourById(tourId: TourId): Promise<TourDefinition | null> {
  const { data, error } = await supabase
    .from('tour_definitions')
    .select('*')
    .eq('id', tourId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return data as TourDefinition;
}

// ============================================================================
// User Onboarding Status
// ============================================================================

export async function getUserOnboarding(userId: string): Promise<UserOnboarding | null> {
  const { data, error } = await supabase
    .from('user_onboarding')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    // Handle common errors gracefully
    if (error.code === 'PGRST116' || error.code === '406') return null;
    logger.error('Error fetching user onboarding:', error);
    return null;
  }
  return data as UserOnboarding | null;
}

export async function ensureUserOnboarding(userId: string): Promise<UserOnboarding> {
  // Try to get existing record
  const existing = await getUserOnboarding(userId);
  if (existing) return existing;

  // Create new record
  const { data, error } = await supabase
    .from('user_onboarding')
    .insert({ user_id: userId })
    .select()
    .single();

  if (error) throw error;
  return data as UserOnboarding;
}

export async function markWelcomeSeen(userId: string): Promise<void> {
  await ensureUserOnboarding(userId);

  const { error } = await supabase
    .from('user_onboarding')
    .update({
      has_seen_welcome: true,
      updated_at: new Date().toISOString()
    })
    .eq('user_id', userId);

  if (error) throw error;
}

export async function markFullTourCompleted(userId: string): Promise<void> {
  await ensureUserOnboarding(userId);

  const { error } = await supabase
    .from('user_onboarding')
    .update({
      full_tour_completed: true,
      full_tour_completed_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })
    .eq('user_id', userId);

  if (error) throw error;
}

// ============================================================================
// User Tour Progress
// ============================================================================

export async function getUserTourProgress(
  userId: string,
  tourId: TourId
): Promise<UserTourProgress | null> {
  const { data, error } = await supabase
    .from('user_tour_progress')
    .select('*')
    .eq('user_id', userId)
    .eq('tour_id', tourId)
    .maybeSingle();

  if (error) {
    // Handle common errors gracefully
    if (error.code === 'PGRST116' || error.code === '406') return null;
    logger.error('Error fetching tour progress:', error);
    return null;
  }
  return data as UserTourProgress | null;
}

export async function getAllUserProgress(userId: string): Promise<UserTourProgress[]> {
  const { data, error } = await supabase
    .from('user_tour_progress')
    .select('*')
    .eq('user_id', userId);

  if (error) throw error;
  return data as UserTourProgress[];
}

export async function startTour(userId: string, tourId: TourId): Promise<UserTourProgress> {
  // Check if progress already exists
  const existing = await getUserTourProgress(userId, tourId);

  if (existing) {
    // Reset progress if already exists
    const { data, error } = await supabase
      .from('user_tour_progress')
      .update({
        current_step: 0,
        completed: false,
        completed_at: null,
        started_at: new Date().toISOString()
      })
      .eq('id', existing.id)
      .select()
      .single();

    if (error) throw error;
    return data as UserTourProgress;
  }

  // Create new progress record
  const { data, error } = await supabase
    .from('user_tour_progress')
    .insert({
      user_id: userId,
      tour_id: tourId,
      current_step: 0,
      completed: false
    })
    .select()
    .single();

  if (error) throw error;

  // Update last tour in onboarding
  await ensureUserOnboarding(userId);
  await supabase
    .from('user_onboarding')
    .update({
      last_tour_id: tourId,
      updated_at: new Date().toISOString()
    })
    .eq('user_id', userId);

  return data as UserTourProgress;
}

export async function advanceStep(userId: string, tourId: TourId): Promise<UserTourProgress> {
  const progress = await getUserTourProgress(userId, tourId);
  if (!progress) throw new Error('Tour progress not found');

  const { data, error } = await supabase
    .from('user_tour_progress')
    .update({
      current_step: progress.current_step + 1
    })
    .eq('id', progress.id)
    .select()
    .single();

  if (error) throw error;
  return data as UserTourProgress;
}

export async function completeTour(userId: string, tourId: TourId): Promise<void> {
  const progress = await getUserTourProgress(userId, tourId);
  if (!progress) throw new Error('Tour progress not found');

  const { error } = await supabase
    .from('user_tour_progress')
    .update({
      completed: true,
      completed_at: new Date().toISOString()
    })
    .eq('id', progress.id);

  if (error) throw error;
}

export async function skipTour(userId: string, tourId: TourId): Promise<void> {
  const progress = await getUserTourProgress(userId, tourId);

  if (progress) {
    const { error } = await supabase
      .from('user_tour_progress')
      .update({
        completed: true,
        completed_at: new Date().toISOString()
      })
      .eq('id', progress.id);

    if (error) throw error;
  } else {
    // Create completed record
    const { error } = await supabase
      .from('user_tour_progress')
      .insert({
        user_id: userId,
        tour_id: tourId,
        current_step: 0,
        completed: true,
        completed_at: new Date().toISOString()
      });

    if (error) throw error;
  }
}

export async function resetTour(userId: string, tourId: TourId): Promise<void> {
  const { error } = await supabase
    .from('user_tour_progress')
    .delete()
    .eq('user_id', userId)
    .eq('tour_id', tourId);

  if (error) throw error;
}

export async function resetAllTours(userId: string): Promise<void> {
  // Delete all progress
  const { error: progressError } = await supabase
    .from('user_tour_progress')
    .delete()
    .eq('user_id', userId);

  if (progressError) throw progressError;

  // Reset onboarding flags
  const { error: onboardingError } = await supabase
    .from('user_onboarding')
    .update({
      has_seen_welcome: false,
      full_tour_completed: false,
      full_tour_completed_at: null,
      last_tour_id: null,
      updated_at: new Date().toISOString()
    })
    .eq('user_id', userId);

  if (onboardingError) throw onboardingError;
}

// ============================================================================
// Helper Functions
// ============================================================================

export function isTourCompleted(
  progress: UserTourProgress[],
  tourId: TourId
): boolean {
  const tourProgress = progress.find(p => p.tour_id === tourId);
  return tourProgress?.completed ?? false;
}

export function getTourProgressPercent(
  progress: UserTourProgress[],
  tourId: TourId,
  totalSteps: number
): number {
  const tourProgress = progress.find(p => p.tour_id === tourId);
  if (!tourProgress) return 0;
  if (tourProgress.completed) return 100;
  return Math.round((tourProgress.current_step / totalSteps) * 100);
}
